import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@byscott-io/corebyscott";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { api } from "../lib/api";

const fmtDate = (d) => d.toISOString().slice(0, 10);

function StatCard({ title, value, sub }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <p className="text-sm text-gray-500">{title}</p>
      <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
      {sub && <p className="text-sm text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

export default function ReferralFunnel() {
  const { currentContainer } = useAuth();
  const [dateFrom, setDateFrom] = useState(fmtDate(new Date(Date.now() - 30 * 86400000)));
  const [dateTo, setDateTo] = useState(fmtDate(new Date()));

  const { data, isLoading, error } = useQuery({
    queryKey: ["referral-funnel", currentContainer?.id, dateFrom, dateTo],
    queryFn: () => api.getReferralFunnel({ date_from: dateFrom, date_to: dateTo }),
    enabled: !!currentContainer,
  });

  if (!currentContainer) {
    return <div className="text-center py-12"><p className="text-gray-500">Loading account...</p></div>;
  }

  const totals = data?.totals || { clicks: 0, signups: 0, conversions: 0, signup_rate: 0, conversion_rate: 0 };
  const perCode = data?.per_code || [];
  const series = data?.time_series || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Referral Funnel</h1>
        <p className="text-gray-500">Clicks → signups → conversions per referral code. Reward amounts live in billing.</p>
      </div>

      {/* Date range */}
      <div className="flex flex-wrap gap-4 items-end bg-white rounded-lg shadow p-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
          <input type="date" value={dateFrom} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
          <input type="date" value={dateTo} min={dateFrom} onChange={(e) => setDateTo(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2" />
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error.message}</div>}

      {isLoading ? (
        <div className="animate-pulse grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => <div key={i} className="bg-gray-200 rounded-lg h-28" />)}
        </div>
      ) : (
        <>
          {/* Funnel stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard title="Clicks" value={totals.clicks.toLocaleString()} sub="Unique referred visitors" />
            <StatCard title="Signups" value={totals.signups.toLocaleString()} sub={`${totals.signup_rate}% of clicks`} />
            <StatCard title="Conversions" value={totals.conversions.toLocaleString()} sub={`${totals.conversion_rate}% of signups`} />
          </div>

          {/* Time series */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Funnel over time (by arrival day)</h2>
            <div className="h-64">
              {series.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No referral activity in this range.</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="clicks" stroke="#6366f1" />
                    <Line type="monotone" dataKey="signups" stroke="#10b981" />
                    <Line type="monotone" dataKey="conversions" stroke="#f59e0b" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Per-code table */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">By code</h2>
            {perCode.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No referral codes with activity in this range.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-2 pr-4 font-medium">Code</th>
                      <th className="py-2 px-4 font-medium text-right">Clicks</th>
                      <th className="py-2 px-4 font-medium text-right">Signups</th>
                      <th className="py-2 px-4 font-medium text-right">Signup rate</th>
                      <th className="py-2 px-4 font-medium text-right">Conversions</th>
                      <th className="py-2 pl-4 font-medium text-right">Conv. rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perCode.map((r) => (
                      <tr key={r.code} className="border-b last:border-0">
                        <td className="py-2 pr-4 font-mono">{r.code}</td>
                        <td className="py-2 px-4 text-right">{r.clicks.toLocaleString()}</td>
                        <td className="py-2 px-4 text-right">{r.signups.toLocaleString()}</td>
                        <td className="py-2 px-4 text-right">{r.signup_rate}%</td>
                        <td className="py-2 px-4 text-right">{r.conversions.toLocaleString()}</td>
                        <td className="py-2 pl-4 text-right">{r.conversion_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
